import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import { TAGS, api } from "@/store/api.ts"
import { IUser, IUserLeaderboardData } from "."
import { IUserBalance } from "../balanceAPI"

const basePath = "/labr-user"

export const userApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.USER, TAGS.BALANCE]
})

export const userEndpoints = userApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserData: builder.query<IUser, void>({
      query: () => ({
        url: `${basePath}/profile`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "DATA" }]
    }),
    getUserSettings: builder.query<IUser, void>({
      query: () => ({
        url: `${basePath}/settings`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "SETTINGS" }]
    }),
    getLabrUserBalance: builder.query<IUserBalance, void>({
      query: () => ({
        url: `${basePath}-balance`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "BALANCE" }]
    }),
    getLeaderboardData: builder.query<IUserLeaderboardData, string>({
      query: (labrUserId) => ({
        url: `${basePath}/leaderboard-data/${labrUserId}`,
        method: "GET"
      }),
      providesTags: (_, __, labrUserId) => [
        { type: TAGS.USER, id: "LEADERBOARD_DATA" },
        { type: TAGS.USER, id: `LEADERBOARD_DATA:${labrUserId}` }
      ]
    })
  })
})

export const {
  useGetUserDataQuery,
  useGetUserSettingsQuery,
  useGetLeaderboardDataQuery,
  useGetLabrUserBalanceQuery,
  endpoints: { getUserData, getUserSettings }
} = userEndpoints

/**
 * Narrowly patches `wallets` in the getUserData cache to preserve any
 * other local patches on the IUser object (balance, inQueue, transient
 * flags). Overwriting the entire cache via `() => user` would drop
 * server-not-returned fields and local-only patches applied earlier in
 * the session.
 *
 * Lives here — not in withdrawalAPI — so deposit / wallet / future flows
 * share one mutation surface and userAPI owns all cache writes to
 * getUserData.
 */
type PatchUserWalletsDispatch = ThunkDispatch<RootState, unknown, UnknownAction>

export const patchUserWallets = (
  dispatch: PatchUserWalletsDispatch,
  wallets: IUser["wallets"]
) => {
  dispatch(
    userEndpoints.util.updateQueryData("getUserData", undefined, (draft) => {
      draft.wallets = wallets
    })
  )
}
